import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useI18n } from '../i18n';
import Turnstile, { type TurnstileStatus } from '../components/Turnstile';
import { validatePasswordStrength } from '../lib/password-policy';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

type Mode = 'login' | 'register';

interface FieldErrors {
  name?: string;
  email?: string;
  password?: string;
}

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const { t } = useI18n();

  const [mode, setMode] = useState<Mode>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tsToken, setTsToken] = useState('');
  const [tsKey, setTsKey] = useState(0);
  const [tsLoadError, setTsLoadError] = useState(false);
  const [tsStatus, setTsStatus] = useState<TurnstileStatus>('loading');

  const captchaRequired = !!TURNSTILE_SITE_KEY;

  function validate(): boolean {
    const e: FieldErrors = {};
    if (mode === 'register' && !name.trim()) {
      e.name = t('auth.errName');
    }
    if (!email.includes('@')) {
      e.email = t('auth.errEmail');
    }
    if (mode === 'register') {
      // 注册：新密码强制复杂度
      const pwdError = validatePasswordStrength(password);
      if (pwdError) e.password = t(pwdError);
    } else if (!password) {
      // 登录：仅查非空，存量老密码不受新规则限制
      e.password = t('auth.errPasswordRequired');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setSubmitError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(email, password, tsToken || undefined);
      } else {
        await register(email, password, name.trim(), tsToken || undefined);
      }
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setSubmitError((err as Error).message || t('auth.errFailed'));
      // token 已被服务端消费，重挂载 widget 获取新 token
      setTsToken('');
      setTsStatus('loading');
      setTsKey((k) => k + 1);
    } finally {
      setSubmitting(false);
    }
  }

  function switchMode(next: Mode) {
    setMode(next);
    setErrors({});
    setSubmitError('');
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-tabs">
          <button
            type="button"
            className={`auth-tab${mode === 'login' ? ' active' : ''}`}
            onClick={() => switchMode('login')}
          >
            {t('auth.tabLogin')}
          </button>
          <button
            type="button"
            className={`auth-tab${mode === 'register' ? ' active' : ''}`}
            onClick={() => switchMode('register')}
          >
            {t('auth.tabRegister')}
          </button>
        </div>

        <h1 className="auth-title">
          {mode === 'login' ? t('auth.loginTitle') : t('auth.registerTitle')}
        </h1>
        <p className="auth-subtitle">
          {mode === 'login' ? t('auth.loginSub') : t('auth.registerSub')}
        </p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          {mode === 'register' && (
            <div className="form-group">
              <label className="form-label" htmlFor="auth-name">
                {t('auth.name')} <span className="required">*</span>
              </label>
              <input
                id="auth-name"
                className="form-input"
                type="text"
                placeholder={t('auth.namePlaceholder')}
                value={name}
                maxLength={40}
                autoComplete="name"
                disabled={submitting}
                onChange={(e) => setName(e.target.value)}
              />
              {errors.name && <div className="field-error">{errors.name}</div>}
            </div>
          )}

          <div className="form-group">
            <label className="form-label" htmlFor="auth-email">
              {t('auth.email')} <span className="required">*</span>
            </label>
            <input
              id="auth-email"
              className="form-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              autoComplete="email"
              disabled={submitting}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && <div className="field-error">{errors.email}</div>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="auth-password">
              {t('auth.password')} <span className="required">*</span>
            </label>
            <input
              id="auth-password"
              className="form-input"
              type="password"
              placeholder={t('auth.passwordPlaceholder')}
              value={password}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              disabled={submitting}
              onChange={(e) => setPassword(e.target.value)}
            />
            {errors.password && (
              <div className="field-error">{errors.password}</div>
            )}
            {mode === 'register' && !errors.password && (
              <div className="form-hint">{t('auth.pwdHint')}</div>
            )}
          </div>

          {mode === 'login' && (
            <p style={{ textAlign: 'right', marginTop: '-0.5rem', marginBottom: '0.5rem' }}>
              <Link to="/forgot-password" style={{ fontSize: '0.82rem', color: 'var(--accent)' }}>
                {t('auth.forgotLink')}
              </Link>
            </p>
          )}

          {captchaRequired && (
            <Turnstile
              key={tsKey}
              siteKey={TURNSTILE_SITE_KEY}
              onToken={setTsToken}
              onLoadError={() => setTsLoadError(true)}
              onStatus={setTsStatus}
            />
          )}
          {captchaRequired && tsLoadError && (
            <div className="field-error" style={{ marginBottom: '0.75rem' }}>
              {t('auth.captchaLoadFailed')}
            </div>
          )}
          {captchaRequired && !tsLoadError && !tsToken && (
            <div
              className="form-hint"
              style={{ marginTop: '-0.5rem', marginBottom: '0.75rem' }}
            >
              {tsStatus === 'timeout' ? t('auth.captchaSlow') : t('auth.captchaPreparing')}
            </div>
          )}

          {submitError && (
            <div className="state-box state-error">{submitError}</div>
          )}

          <button
            className="btn btn-primary btn-lg auth-submit"
            type="submit"
            disabled={submitting || (captchaRequired && !tsToken)}
          >
            {submitting
              ? t('auth.submitting')
              : mode === 'login'
                ? t('auth.submitLogin')
                : t('auth.submitRegister')}
          </button>
        </form>
      </div>
    </div>
  );
}
